import { GqlLineItem } from '../queries/sale_create'

export enum SaleStatus {
  CLOSED = 'CLOSED',
  PARKED = 'SAVED',
  VOIDED = 'VOIDED',
  ACCOUNT_SALE = 'ONACCOUNT',
  ACCOUNT_SALE_CLOSED = 'ONACCOUNT_CLOSED',
  LAYBY_SALE = 'LAYBY',
  LAYBY_SALE_CLOSED = 'LAYBY_CLOSED',
  RETURN_PARKED = 'RETURN_PARKED',
  RETURN_CLOSED = 'RETURN_CLOSED',
  EXCHANGE_PARKED = 'EXCHANGE_PARKED',
  EXCHANGE_CLOSED = 'EXCHANGE_CLOSED',
  DISPATCHED_CLOSED = 'DISPATCHED_CLOSED',
  PICKED_UP_CLOSED = 'PICKED_UP_CLOSED',
  QUOTE = 'QUOTE',
  AWAITING_DISPATCH = 'AWAITING_DISPATCH',
  AWAITING_PICKUP = 'AWAITING_PICKUP',
  SERVICE = 'SERVICE',
  SERVICE_CLOSED = 'SERVICE_CLOSED',
}

// TODO: We should create a shared utility between Sales history and Web
// register to avoid having a copy
export const DISPLAY_STATUSES: Record<string, string> = {
  [SaleStatus.CLOSED]: 'Completed',
  [SaleStatus.PARKED]: 'Parked',
  [SaleStatus.VOIDED]: 'Voided',
  [SaleStatus.ACCOUNT_SALE]: 'On Account',
  [SaleStatus.ACCOUNT_SALE_CLOSED]: 'On Account, completed',
  [SaleStatus.LAYBY_SALE]: 'Layby',
  [SaleStatus.LAYBY_SALE_CLOSED]: 'Layby, completed',
  [SaleStatus.RETURN_PARKED]: 'Parked Return',
  [SaleStatus.RETURN_CLOSED]: 'Return, completed',
  [SaleStatus.EXCHANGE_PARKED]: 'Parked Exchange',
  [SaleStatus.EXCHANGE_CLOSED]: 'Exchange, completed',
  [SaleStatus.QUOTE]: 'Quote',
  [SaleStatus.AWAITING_PICKUP]: 'Pickup, unfulfilled',
  [SaleStatus.AWAITING_DISPATCH]: 'Delivery, unfulfilled',
  [SaleStatus.PICKED_UP_CLOSED]: 'Pickup, completed',
  [SaleStatus.DISPATCHED_CLOSED]: 'Delivery, completed',
}

export const CONFIRMED_STATUSES = [
  SaleStatus.ACCOUNT_SALE,
  SaleStatus.LAYBY_SALE,
]

export const UNFULFILLED_STATUSES = [
  SaleStatus.AWAITING_DISPATCH,
  SaleStatus.AWAITING_PICKUP,
]

export const CONFIRMED_STATUSES_CLOSED = [
  SaleStatus.ACCOUNT_SALE_CLOSED,
  SaleStatus.LAYBY_SALE_CLOSED,
]

export const CLOSED_STATUSES = [
  SaleStatus.CLOSED,
  SaleStatus.ACCOUNT_SALE_CLOSED,
  SaleStatus.LAYBY_SALE_CLOSED,
  SaleStatus.EXCHANGE_CLOSED,
  SaleStatus.RETURN_CLOSED,
  SaleStatus.DISPATCHED_CLOSED,
  SaleStatus.PICKED_UP_CLOSED,
  SaleStatus.SERVICE_CLOSED,
]

export const COMPLETED_STATUSES = CLOSED_STATUSES.concat(SaleStatus.VOIDED)

export const OPEN_STATUSES = [
  SaleStatus.LAYBY_SALE,
  SaleStatus.ACCOUNT_SALE,
  SaleStatus.PARKED,
  SaleStatus.EXCHANGE_PARKED,
  SaleStatus.RETURN_PARKED,
  SaleStatus.AWAITING_DISPATCH,
  SaleStatus.AWAITING_PICKUP,
  SaleStatus.SERVICE,
]

export const ALLOW_RETURN_STATUSES = COMPLETED_STATUSES.concat(
  SaleStatus.AWAITING_DISPATCH,
  SaleStatus.AWAITING_PICKUP,
  SaleStatus.SERVICE
)

// Used to determine if the retailer should be prompted for a sale note when the sale confirmation view is shown. At
// this stage in the sale life cycle the status may or may not be closed for Account / Laybys, but always closed for
// Exchanges and Returns.
export const ASK_FOR_NOTE_STATUSES = [
  SaleStatus.ACCOUNT_SALE,
  SaleStatus.ACCOUNT_SALE_CLOSED,
  SaleStatus.LAYBY_SALE,
  SaleStatus.LAYBY_SALE_CLOSED,
  SaleStatus.EXCHANGE_CLOSED,
  SaleStatus.RETURN_CLOSED,
  SaleStatus.AWAITING_DISPATCH,
  SaleStatus.AWAITING_PICKUP,
  SaleStatus.QUOTE,
  SaleStatus.SERVICE,
]

export const GIFT_RECEIPT_STATUSES = [
  SaleStatus.CLOSED,
  SaleStatus.ACCOUNT_SALE_CLOSED,
  SaleStatus.LAYBY_SALE_CLOSED,
  SaleStatus.EXCHANGE_CLOSED,
  SaleStatus.AWAITING_DISPATCH,
  SaleStatus.AWAITING_PICKUP,
  SaleStatus.SERVICE,
]

export enum SaleSyncStatus {
  ATTEMPTED = 'ATTEMPTED',
  UNMODIFIED = 'UNMODIFIED',
  MODIFIED = 'MODIFIED',
  UPLOAD = 'UPLOAD',
  UPLOAD_FIRST_SUBMIT = 'UPLOAD_FIRST_SUBMIT',
  UPLOADED = 'UPLOADED',
  UPLOADED_TO_COMPLETE = 'UPLOADED_TO_COMPLETE',
  ERROR = 'ERROR',
}

export const SYNCED_SYNC_STATUSES = [
  // Has not been modified on the client so is in sync with server
  SaleSyncStatus.UNMODIFIED,
  // Has been modified on client but changes have been sent to server (and accepted via 200 response)
  SaleSyncStatus.UPLOADED,
]

export const UNSYNCED_SYNC_STATUSES = [
  SaleSyncStatus.ATTEMPTED,
  SaleSyncStatus.UPLOAD,
  SaleSyncStatus.ERROR,
]

export const RETRY_SYNC_STATUSES = [
  SaleSyncStatus.ATTEMPTED,
  SaleSyncStatus.UPLOAD,
]

export enum SaleState {
  OPEN = 'OPEN',
  PARKED = 'PARKED',
  AWAITING_CONFIRM = 'AWAITING_CONFIRM',
  CONFIRMED = 'CONFIRMED',
  CLOSED = 'CLOSED',
  VOIDED = 'VOIDED',
}

export enum SaleType {
  REGULAR = 'REGULAR',
  ONACCOUNT = 'ONACCOUNT',
  LAYBY = 'LAYBY',
}

export enum SalePaymentState {
  PENDING = 'PENDING',
  PARTIAL = 'PARTIAL',
  COMPLETE = 'COMPLETE',
}

export enum SaleFulfilmentState {
  PENDING = 'PENDING',
  PARTIAL = 'PARTIAL',
  COMPLETE = 'COMPLETE',
}

export const PARK_SALE_FAILED_ERROR_MSG = 'park sale failed'

export enum SaleCreateErrorCode {
  INTERNAL = 'INTERNAL',
  EXISTS = 'EXISTS',
  NOT_FOUND = 'NOT_FOUND',
  INVALID = 'INVALID',
  PERMISSION = 'PERMISSION',
  CONNECTION = 'CONNECTION',
  TIMEOUT = 'TIMEOUT',
}

export enum SaleCreateErrorField {
  SALE_ID = 'SALE_ID',
  MUTATION_ID = 'MUTATION_ID',
  REGISTER_ID = 'REGISTER_ID',
  USER_ID = 'USER_ID',
  PRODUCT_ID = 'PRODUCT_ID',
  TAX_ID = 'TAX_ID',
  PROMOTION_ID = 'PROMOTION_ID',
  PAYMENT_TYPE_ID = 'PAYMENT_TYPE_ID',
}

/**
 * Type of User in Sale. Not a complete representation of the User. Add as needed
 */

export interface User {
  id: string
  username: string
  display_name: string
  roles?: UserRole[]
}

export interface UserRole {
  id: string
  name: string
  system_role_id: string
}

export enum ServiceActions {
  // Sorry for _JOB, it is what BE uses
  CREATE_JOB = 'CREATE_JOB',
  UPDATE_JOB = 'UPDATE_JOB',
}
export interface ServiceAgenda {
  date: string
  minutesScheduled: number
}

export interface ServiceWithAgenda {
  id: string
  status: ServiceStatusProtobuf // consider that in the future we might need to also add a type for statuses without the JOB_STATUS_ prefix
  agenda: ServiceAgenda
}

export interface ServiceItem {
  itemId?: string
  itemName?: string // mandatory in the UI
  serialNumber?: string
  description?: string
  initialCondition?: string
}
export interface ServiceItemData {
  itemDetails?: ServiceItem
  location?: string
}

export interface ServiceSaleLevelDiscount {
  isPercentage: boolean
  value: string
}

// When receiving the data from graphter, the structure is different
export interface ServiceItemResponseData {
  serviceItem: ServiceItem
  notes: ServiceNote[]
}

interface ServiceNote {
  body: string
  location: string
}
// This is how the status is stored in the BE
export enum ServiceStatusProtobuf {
  UNSPECIFIED = 'JOB_STATUS_UNSPECIFIED',
  NEW = 'JOB_STATUS_NEW',
  READY_TO_START = 'JOB_STATUS_READY_TO_START',
  IN_PROGRESS = 'JOB_STATUS_IN_PROGRESS',
  AWAITING_CUSTOMER = 'JOB_STATUS_AWAITING_CUSTOMER',
  AWAITING_PART = 'JOB_STATUS_AWAITING_PART',
  COMPLETED = 'JOB_STATUS_COMPLETED',
  CANCELLED = 'JOB_STATUS_CANCELLED',
  DELETED = 'JOB_STATUS_DELETED',
}

export enum ServiceFinalStatus {
  UNSPECIFIED = ServiceStatusProtobuf.UNSPECIFIED,
  COMPLETED = ServiceStatusProtobuf.COMPLETED,
  CANCELLED = ServiceStatusProtobuf.CANCELLED,
  DELETED = ServiceStatusProtobuf.DELETED,
}

export interface ServiceFields {
  status?: ServiceStatusProtobuf
  serviceAction?: ServiceActions
  serviceAgenda?: ServiceAgenda
  serviceItem?: ServiceItemData
  serviceLineItems?: GqlLineItem[]
  saleLevelDiscount?: ServiceSaleLevelDiscount
}

export const SERVICE_ORDERS_CUSTOM_FIELD = 'internal/sale-has-service-orders'

export const SaleAttributes = {
  FULFILMENT_DELIVERY: 'delivery',
  FULFILMENT_PICKUP: 'pickup',
}

export interface SaleMetadata {
  key: string
  value: string
}
